import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeLink } from '@/components/ui/tee-link';
import { authService } from '@/services/auth/auth.service';
import { IconMailIcon } from '@/components/ui/icons';
import styles from './LoginPage.module.css';
import { TeeInput } from '@/components/ui/tee-input';
import { TeeDigitInput } from '@/components/ui/tee-digit-input';
import { IconBrandGoogleFilled } from '@tabler/icons-react';
import { resolveLandingRoute, readStoredUser } from '@/utils/roleRouting';

export default function LoginPageLegacy() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [showEmailForm, setShowEmailForm] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;
    try {
      const { exp } = JSON.parse(atob(token.split('.')[1]));
      if (Date.now() / 1000 < exp) {
        navigate(resolveLandingRoute(readStoredUser()));
      } else {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      }
    } catch {
      // Malformed token — clear and show login
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
    }
  }, [navigate]);

  useEffect(() => {
    if (!showOtpInput) {
      setTimeLeft(0);
      return;
    }

    setTimeLeft(600);

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setShowOtpInput(false);
          toast.error('OTP expired. Please request a new one.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showOtpInput]);

  const handleGoogleLogin = async () => {
    try {
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!clientId) {
        toast.error('Google login is not configured. Please contact support.');
        return;
      }
      const redirectUri =
        import.meta.env.VITE_GOOGLE_REDIRECT_URI ||
        `${window.location.origin}/auth/google/callback`;
      const scope = 'openid email profile';

      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.append('client_id', clientId);
      authUrl.searchParams.append('redirect_uri', redirectUri);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('scope', scope);
      authUrl.searchParams.append('prompt', 'select_account');
      authUrl.searchParams.append('access_type', 'offline');

      window.location.href = authUrl.toString();
    } catch (error: any) {
      toast.error('Failed to initiate Google login');
    }
  };

  const requestOtp = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    try {
      setIsLoading(true);
      await authService.requestOtp(email);
      setShowOtpInput(true);
      toast.success('OTP sent to your email');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestOtp = (e: React.FormEvent) => {
    e.preventDefault();
    requestOtp();
  };

  const verifyOtp = async (code: string) => {
    if (!code) {
      toast.error('Please enter the OTP');
      return;
    }
    try {
      setIsLoading(true);
      const response = await authService.verifyOtp(email, code);
      localStorage.setItem('adminToken', response.token);
      localStorage.setItem('adminUser', JSON.stringify(response.user));
      window.dispatchEvent(new Event('authChange'));
      toast.success('Login successful!');
      navigate(resolveLandingRoute(response.user));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    /*
     * Background: drop a golf course photo at box-os/public/golf-bg.jpg to activate.
     * Falls back to a deep-green gradient when the image is absent.
     */
    <div
      className={`${styles['login-page-bg']} min-h-screen flex flex-col items-center justify-center relative overflow-hidden`}
    >
      {/* Subtle gold top accent */}
      <div className="absolute top-0 left-0 right-0 h-[4px] bg-[var(--color-gold-500)] z-10" />

      <div className="relative z-10 w-full flex items-center justify-center px-6 py-16">
        <div className={styles['login-container']}>
          {/* Logo — the white brand SVG darkened to ink for the cream card */}
          <div className="flex justify-center">
            <div className="h-[46px] w-[173px] overflow-clip">
              <img
                src="/assets/logo-white.svg"
                alt="Tee Box Logo"
                className="block h-full w-full object-contain"
                style={{ filter: 'brightness(0)' }}
              />
            </div>
          </div>

          {/* Form Container */}
          <div className="flex w-full flex-col gap-l">
            {!showOtpInput ? (
              <>
                {/* Google Login — primary CTA */}
                <TeeButton
                  type="button"
                  variant="primary"
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  leftIcon={<IconBrandGoogleFilled size={16} />}
                >
                  Continue with Google
                </TeeButton>

                {/* Email escape hatch */}
                {!showEmailForm ? (
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={() => setShowEmailForm(true)}
                      className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      or sign in with email
                    </button>
                  </div>
                ) : (
                  <div className="flex w-full flex-col gap-6">
                    {/* Divider */}
                    <div className="relative w-full flex items-center gap-4">
                      <div className="flex-1 border-t border-neutral-200" />
                      <span className={styles['or-label']}>or continue with email</span>
                      <div className="flex-1 border-t border-neutral-200" />
                    </div>

                    <h2 className="text-xl font-semibold text-gray-900 text-center">Login with your email</h2>

                    {/* Email Form */}
                    <form onSubmit={handleRequestOtp} className="w-full flex flex-col gap-4">
                      <TeeInput
                        id="email"
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        leadingIcon={<IconMailIcon size={16} color="#6C756D" />}
                        disabled={isLoading}
                        required
                      />
                      <TeeButton type="submit" variant="secondary" disabled={isLoading}>
                        {isLoading ? 'Sending OTP...' : 'Continue with email'}
                      </TeeButton>
                    </form>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* OTP Title */}
                <div className="flex w-full flex-col gap-6 items-center">
                  <h2 className="text-xl font-semibold text-gray-900">Verify your code</h2>

                  {/* OTP Description */}
                  <div className="flex flex-col gap-4 items-center w-full">
                    <p className="text-center text-sm text-gray-600">
                      Please confirm your account by entering the authorization
                      code sent to
                      <br />
                      <span className="font-medium text-gray-900">{email}</span>
                    </p>
                    <p className="text-center text-sm text-gray-600">
                      This code will expire in {Math.floor(timeLeft / 60)}:
                      {String(timeLeft % 60).padStart(2, '0')} minutes.
                    </p>
                  </div>

                  {/* OTP Form */}
                  <form
                    className="w-full flex flex-col gap-6"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    {/* OTP Digit Input */}
                    <div className="flex justify-center">
                      <TeeDigitInput
                        length={6}
                        value={otp}
                        onChange={setOtp}
                        onComplete={(val) => verifyOtp(val)}
                        placeholder="•"
                      />
                    </div>

                    {/* Helper Text */}
                    <div className="flex flex-col gap-2 items-center w-full">
                      <p className="text-center text-sm text-gray-600">
                        Allow a few minutes and check your spam folder.
                      </p>
                      <p className="text-center text-sm text-gray-600">
                        Haven't received it?{' '}
                        <TeeLink
                          variant="primary"
                          size="s"
                          href="#"
                          onClick={(e) => { e.preventDefault(); requestOtp(); }}
                        >
                          Resend a new code
                        </TeeLink>
                        .
                      </p>
                    </div>

                    {/* Back Button */}
                    <TeeButton
                      type="button"
                      variant="secondary"
                      size="s"
                      onClick={() => {
                        setShowOtpInput(false);
                        setOtp('');
                        setShowEmailForm(false);
                      }}
                      disabled={isLoading}
                      className="w-full"
                    >
                      Back
                    </TeeButton>
                  </form>


                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

